"use client";

import { useEffect, useSyncExternalStore } from "react";
import type { ReactNode } from "react";

import { maintenanceScreenCopy } from "./screen-copy";
import type { MaintenanceScreenCopy } from "./screen-copy";
import { watchForReopening } from "./reopen-watch";
import type { MaintenanceStore } from "./store";

type MaintenanceBoundaryProps = {
  readonly store: MaintenanceStore;
  /** Draws the full-screen scene. Without it the app is left as it is. */
  readonly render?: (screen: MaintenanceScreenCopy) => ReactNode;
  readonly children: ReactNode;
};

/**
 * Swaps the app for the "Back soon" screen once any call is refused for maintenance. While it shows, it
 * asks the app a cheap question every `Retry-After` seconds and reloads the page when the answer is no longer 503.
 */
export function MaintenanceBoundary({ store, render, children }: MaintenanceBoundaryProps) {
  const notice = useSyncExternalStore(store.subscribe, store.get, store.get);
  const showing = notice !== undefined && render !== undefined;

  useEffect(() => {
    const first = store.get();
    if (!showing || first === undefined) return undefined;
    return watchForReopening({ notice: first, onNotice: store.report, onReopened: () => window.location.reload() });
  }, [showing, store]);

  return showing ? render(maintenanceScreenCopy(notice)) : children;
}
